import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import {
  type BillingInterval,
  type BillingSummary,
  intervalFor,
  type PlanId,
  unitPrice,
} from "@websede/contracts";
import { Check, CreditCard, ExternalLink } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client.js";
import { trialDaysLeft } from "../../components/billing-banner.js";
import { WorkspaceDataCards } from "../../components/data-controls.js";
import { Button, Card, ErrorNotice } from "../../components/form.js";

const PLAN_ORDER: PlanId[] = ["personal", "team"];

/** Plan, trial, seats and price; choosing or switching a plan; the provider's billing portal. */
export function AdminBillingPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const search = useSearch({ from: "/admin/billing" });
  const justPaid = search.checkout === "done";
  const summary = useQuery({
    queryKey: ["billing", "summary"],
    queryFn: () => api.billing.summary(),
    // Right after checkout the provider's webhook may still be on its way.
    refetchInterval: (query) => (justPaid && !query.state.data?.hasPaymentMethod ? 2_000 : false),
  });
  const s = summary.data;
  const [interval, setChosenInterval] = useState<BillingInterval | null>(null);
  const shownInterval = interval ?? s?.interval ?? "month";
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["billing"] });
  };
  const choose = useMutation({
    mutationFn: (plan: PlanId) =>
      api.billing.choosePlan({ plan, interval: intervalFor(plan, shownInterval) }),
    onSuccess: (result) => {
      if (result.url) window.location.assign(result.url);
      else refresh();
    },
  });
  const portal = useMutation({
    mutationFn: () => api.billing.portal(),
    onSuccess: (result) => window.location.assign(result.url),
  });
  const money = (minor: number) =>
    new Intl.NumberFormat(i18n.language, { style: "currency", currency: "USD" }).format(
      minor / 100,
    );
  const date = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "long" }).format(new Date(iso));

  return (
    <section className="mx-auto flex w-full max-w-4xl flex-col gap-6 p-4 md:p-8">
      <h1 className="text-[22px] text-ink">{t("admin.nav.billing")}</h1>
      <ErrorNotice error={summary.error ?? choose.error ?? portal.error} />
      {justPaid && s && !s.hasPaymentMethod ? (
        <p className="rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent">
          {t("billing.activating")}
        </p>
      ) : null}
      {s?.complimentary ? (
        <>
          <Card title={t("billing.complimentary.title")}>
            <p className="text-sm text-ink-2">{t("billing.complimentary.body")}</p>
          </Card>
          <WorkspaceDataCards />
        </>
      ) : s ? (
        <>
          <CurrentPlan
            summary={s}
            money={money}
            date={date}
            onPortal={() => portal.mutate()}
            portalPending={portal.isPending}
          />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-base font-medium text-ink">{t("billing.plans")}</h2>
            <div className="flex rounded-full border border-rule p-0.5 text-sm">
              {(["month", "year"] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={shownInterval === value}
                  onClick={() => setChosenInterval(value)}
                  className={`rounded-full px-4 py-1.5 ${shownInterval === value ? "bg-accent text-white" : "text-ink-2 hover:bg-ink/5"}`}
                >
                  {value === "month" ? t("billing.monthly") : t("billing.yearly")}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {PLAN_ORDER.map((id) => {
              const billed = intervalFor(id, shownInterval);
              const running = s.hasPaymentMethod && s.access !== "expired";
              const current = running && s.plan === id && s.interval === billed;
              const price = unitPrice(id, billed);
              return (
                <article
                  key={id}
                  className={`flex flex-col gap-4 rounded-2xl border bg-surface p-5 md:p-6 ${current ? "border-accent" : "border-rule"}`}
                >
                  <div>
                    <h3 className="text-lg font-medium text-ink">{t(`billing.plan.${id}.name`)}</h3>
                    <p className="text-sm text-ink-3">{t(`billing.plan.${id}.tagline`)}</p>
                  </div>
                  <p className="flex items-baseline gap-1">
                    <span className="text-3xl text-ink">{money(price)}</span>
                    <span className="text-sm text-ink-3">
                      {billed === "year"
                        ? t("billing.perMailboxYear")
                        : t("billing.perMailboxMonth")}
                    </span>
                  </p>
                  {billed !== shownInterval ? (
                    <p className="-mt-3 text-xs text-ink-3">
                      {t("billing.yearlyOnly", { amount: money(Math.round(price / 12)) })}
                    </p>
                  ) : null}
                  <ul className="flex flex-1 flex-col gap-2 text-sm text-ink-2">
                    {(t(`billing.plan.${id}.features`, { returnObjects: true }) as string[]).map(
                      (feature) => (
                        <li key={feature} className="flex items-start gap-2">
                          <Check className="mt-0.5 size-4 shrink-0 text-good" />
                          {feature}
                        </li>
                      ),
                    )}
                  </ul>
                  <Button
                    variant={current ? "secondary" : "primary"}
                    disabled={current || choose.isPending}
                    onClick={() => {
                      if (running && !window.confirm(t("billing.confirmSwitch"))) return;
                      choose.mutate(id);
                    }}
                  >
                    {current
                      ? t("billing.current")
                      : running
                        ? t("billing.switchTo", { plan: t(`billing.plan.${id}.name`) })
                        : t("billing.choose", { plan: t(`billing.plan.${id}.name`) })}
                  </Button>
                </article>
              );
            })}
          </div>
          <p className="text-xs text-ink-3">{t("billing.footnote")}</p>
          <WorkspaceDataCards />
        </>
      ) : null}
    </section>
  );
}

function CurrentPlan({
  summary: s,
  money,
  date,
  onPortal,
  portalPending,
}: {
  summary: BillingSummary;
  money: (minor: number) => string;
  date: (iso: string) => string;
  onPortal: () => void;
  portalPending: boolean;
}) {
  const { t } = useTranslation();
  const unit = s.amountMinor / s.seats;
  const period = s.interval === "year" ? t("billing.perYear") : t("billing.perMonth");
  const status: Record<BillingSummary["access"], { label: string; tone: string }> = {
    trial: { label: t("billing.access.trial"), tone: "bg-accent-soft text-accent" },
    active: { label: t("billing.access.active"), tone: "bg-good-soft text-good" },
    past_due: { label: t("billing.access.past_due"), tone: "bg-danger-soft text-danger" },
    expired: { label: t("billing.access.expired"), tone: "bg-danger-soft text-danger" },
  };
  let when: string | null = null;
  if (s.access === "trial" && s.trialEndsAt) {
    when = t("billing.trialEnds", {
      date: date(s.trialEndsAt),
      count: trialDaysLeft(s),
    });
  } else if (s.currentPeriodEnd) {
    when = s.cancelAtPeriodEnd
      ? t("billing.endsOn", { date: date(s.currentPeriodEnd) })
      : s.firstChargePending
        ? t("billing.firstCharge", { date: date(s.currentPeriodEnd) })
        : t("billing.renewsOn", { date: date(s.currentPeriodEnd) });
  }
  return (
    <Card title={t("billing.yourPlan")}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xl text-ink">{t(`billing.plan.${s.plan}.name`)}</span>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${status[s.access].tone}`}>
          {status[s.access].label}
        </span>
      </div>
      <p className="text-sm text-ink-2">
        {t("billing.seatsLine", {
          count: s.seats,
          unit: money(unit),
          total: money(s.amountMinor),
          period,
        })}
      </p>
      {when ? <p className="text-sm text-ink-3">{when}</p> : null}
      {s.hasPaymentMethod || s.access === "past_due" ? (
        <div>
          <Button variant="secondary" disabled={portalPending} onClick={onPortal}>
            <CreditCard className="size-4" />
            {t("billing.manage")}
            <ExternalLink className="size-3.5" />
          </Button>
        </div>
      ) : (
        <p className="text-sm text-ink-3">{t("billing.noCardYet")}</p>
      )}
    </Card>
  );
}
